import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Controller, useFormContext } from 'react-hook-form';
import { $recipientMandatoryFieldsStore } from '@lib/store';
import { cn, PaletteUtils, ValidateUtils } from '@shared/utils';
import { BaseInput, ErrorMessage, Text } from '@shared/widgets';

export interface PersonalFormData {
  firstName: string;
  lastName: string;
  idNumber: string; // insi_idnumber
  email: string; // insi_email
}

interface PersonalFormBlockProps {
  className?: string;
}

export const PersonalFormBlock = (props: PersonalFormBlockProps) => {
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);

  const { control } = useFormContext<PersonalFormData>();

  const { t } = useTranslation('payments');

  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('form.titles.personalInformation')}</Text>
      <Controller
        name='firstName'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.alphaNumericFormat', { ns: 'common', field: 'First Name' }) },
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <BaseInput
              {...field}
              label={t('placeholders.firstName', { ns: 'common' })}
              placeholder={t('placeholders.firstName', { ns: 'common' })}
              required
            />
            <ErrorMessage text={error?.message} />
          </div>
        )}
      />
      <Controller
        name='lastName'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.alphaNumericFormat', { ns: 'common', field: 'Last Name' }) },
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <BaseInput
              {...field}
              label={t('placeholders.lastName', { ns: 'common' })}
              placeholder={t('placeholders.lastName', { ns: 'common' })}
              required
            />
            <ErrorMessage text={error?.message} />
          </div>
        )}
      />
      {mandatoryFields.data.includes('insi_idnumber') && (
        <Controller
          name='idNumber'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('form.placeholders.idNumber')} placeholder={t('form.placeholders.idNumber')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_email') && (
        <Controller
          name='email'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput
                {...field}
                type='email'
                label={t('placeholders.email', { ns: 'common' })}
                placeholder={t('placeholders.email', { ns: 'common' })}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      )}
    </div>
  );
};
